import { metersToFeet } from 'utils/distanceUtil';

import { VerticalContainer } from '@american-autonomy/ag-ui';

import CollapsibleSection from 'components/CollapsibleSection';
import StepperField from 'components/StepperField';
import SecondaryButton from 'components/buttons/SecondaryButton';

import LabeledToggle from 'pages/ApplicationDetailsPage/components/FlightPlannerPanel/components/LabeledToggle';
import useApplicationDetailsTranslation from 'pages/ApplicationDetailsPage/hooks/useTranslation';

import useImperialHandlers from './hooks/useImperialHandlers';

import {
    CONSTRAIN_OFF_INDEX,
    CONSTRAIN_ON_INDEX,
    CONSTRAIN_ON_VALUE,
    CONSTRAIN_OPTIONS,
    FIELD_MARGIN,
    LABELS,
    OBSTACLE_SAFETY_DISTANCE,
    TEST_ID,
    VALIDATE_COVERAGE_BUTTON_TEST_ID
} from './constants';
import type { FunctionComponent, Props, RoutePlanConfig } from './types';

import styles from './styles.module.scss';

const AdvancedSection: FunctionComponent<Props> = ({
    children,
    className = '',
    config,
    isOpen,
    onConfigChange,
    onToggle,
    testId = TEST_ID
}) => {
    const { ADVANCED_SPRAY_SETTINGS_TITLE } = useApplicationDetailsTranslation();
    const constrainIndex = config.constrainToBoundary ? CONSTRAIN_ON_INDEX : CONSTRAIN_OFF_INDEX;
    const update = (partial: Partial<RoutePlanConfig>) => onConfigChange({ config: partial });
    const { handleFieldMarginChange, handleObstacleSafetyChange } = useImperialHandlers({
        onUpdate: update
    });
    const handleConstrainClick = (value: string) =>
        update({ constrainToBoundary: value === CONSTRAIN_ON_VALUE });
    const handleValidateClick = () => update({ validateCoverage: true });

    return (
        <CollapsibleSection
            className={`${className} ${styles.advancedSection}`}
            isOpen={isOpen}
            onToggle={onToggle}
            testId={testId}
            title={ADVANCED_SPRAY_SETTINGS_TITLE}>
            <VerticalContainer className={styles.body}>
                <LabeledToggle
                    activeIndex={constrainIndex}
                    label={LABELS.constrainToBoundary}
                    onClick={handleConstrainClick}
                    options={CONSTRAIN_OPTIONS}
                />

                <StepperField
                    label={LABELS.fieldMargin}
                    max={FIELD_MARGIN.max}
                    min={FIELD_MARGIN.min}
                    onChange={handleFieldMarginChange}
                    step={FIELD_MARGIN.step}
                    value={metersToFeet(config.fieldMargin)}
                />

                <StepperField
                    label={LABELS.obstacleSafetyDistance}
                    max={OBSTACLE_SAFETY_DISTANCE.max}
                    min={OBSTACLE_SAFETY_DISTANCE.min}
                    onChange={handleObstacleSafetyChange}
                    step={OBSTACLE_SAFETY_DISTANCE.step}
                    value={metersToFeet(config.obstacleSafetyDistance)}
                />

                <SecondaryButton
                    onClick={handleValidateClick}
                    testId={VALIDATE_COVERAGE_BUTTON_TEST_ID}
                    text={LABELS.validateCoverage}
                />

                {children}
            </VerticalContainer>
        </CollapsibleSection>
    );
};

export default AdvancedSection;
